/**
 * The posting-source registry (GL-1, `docs/domains/gl/posting-rules.md`): every `postingSource` a
 * journal written through `JournalService.postJournalBatch` may carry. An unregistered source is
 * refused with `JOURNAL_POSTING_SOURCE_UNKNOWN` before anything is written.
 *
 * Accountant-facing names (docs/AGENTS.md rule 12): every source here, and every source in
 * {@link DIRECT_WRITE_POSTING_SOURCES} and {@link LEGACY_POSTING_SOURCES}, needs a pt-BR label in
 * `apps/web/src/lib/i18n/gl.ts` (`pages.ledger.grouped.postingSources`). The web golden test
 * `GroupedLedgerPostingRow.test.tsx` enumerates {@link ALL_POSTING_SOURCES} and fails on a source
 * without one.
 */
export const JOURNAL_POSTING_SOURCES = [
  'ap_bill_provision',
  'ap_payment',
  'ap_fx_revaluation',
  'fx_remeasurement',
  'ap_remediation',
  'ap_bill_deferral',
  'ap_bill_deferral_cancellation',
  'ap_bill_deferral_reclassification',
  'ap_bill_deferral_refund',
  'ap_client_recoverable_write_off',
  'ar_invoice_accrual',
  'ar_invoice_reversal',
  /// Standalone reclassification of a recognition-time ISS provision when the NFS-e is
  /// authorized AFTER the invoice was GL-posted (ALC-1332). Distinct from
  /// `ar_invoice_accrual` because it is a later, fiscal-triggered event on an
  /// already-posted invoice, dated at the note's issue date rather than the invoice's.
  'ar_nfse_iss_reclass',
  /// One-off repair (ALC-4435): moves a Contract Asset / Liability balance stranded on a
  /// per-contract child under a root the slot no longer names onto the child the next
  /// invoice or recognition relieves. Posted only by the stranded-balance tenant script.
  'ar_contract_child_reclass',
  'ar_revenue_recognition',
  /// Approving a settlement aggregate: the OPEN balance of the transactions it
  /// bills moves from "serviços prestados a faturar" to Clientes (Dr partner
  /// receivable child / Cr unbilled). No revenue — that was recognised per
  /// transaction by `ar_revenue_recognition`; this is the asset side becoming
  /// a billed receivable once the document exists (C4, GTV model).
  'ar_aggregate_reclass',
  /// Approving a BR contract-period aggregate (ALC-5092): the withholdings its NFS-e states,
  /// booked once on the aggregate (Dr each recoverable withholding / Cr Clientes), so the
  /// customer's receivable is the NF's net. No revenue and no ISS: the transactions carry both.
  'ar_aggregate_withholding',
  /// Cash-basis tax rail, stage 1 → 2: the tax recognised on transactions moves
  /// from "no cobrado" to "por pagar" when the money is in (an acquirer rail
  /// credit, a direct payment, the aggregate's receipt). Dr uncollected / Cr
  /// payable, for exactly what the recognition credited (C5, GTV model).
  'ar_tax_collected',
  /// Custodial settlement bank leg: when the repasse SPEI leaves, clear the
  /// pass-through liability (Dr Repasse a Hoteles por Pagar / Cr Banco). Distinct
  /// from `ar_revenue_recognition`, the earlier recognition/provision leg.
  'ar_settlement_reconciliation',
  /// Acquirer rail pay-in settling the batches it closed: Dr Banco (net) /
  /// Dr Comissões de adquirência / Dr IVA acreditable / Cr Numerários em
  /// trânsito (gross). The inbound mirror of `ar_settlement_reconciliation`.
  'ar_rail_settlement',
  /// Trues the custodial payable up to what a deposit actually settles
  /// (Dr partner payable / Cr in-transit). A settlement report carries
  /// movements that are never billed — a refund, a chargeback retention —
  /// which still reduce the deposit, so recognition credits more than the
  /// deposit pays. Distinct from `ar_revenue_recognition`, which it
  /// corrects, and from `ar_settlement_reconciliation`, which is the later
  /// cash leg: this one touches no bank account.
  'ar_settlement_payable_adjustment',
  /// An internal transfer between two of the entity's own bank accounts
  /// (Dr Banco destino / Cr Banco origem) — no P&L, only moves cash between
  /// the settlement accounts (PAYMENTS -> REVENUE/IVA).
  'ar_internal_transfer',
  'ar_debit_note',
  'ar_receipt',
  'statutory_payment_clearing',
  /// Whole-guide temporary approval posting selected after statutory account resolution fails:
  /// `Dr Tributos a classificar / Cr Guias tributárias a pagar`. The temporary debit remains
  /// visible to the non-blocking accounting review until a bill-linked reclass clears it.
  'statutory_accounting_fallback',
  /// Approval-time provision for an `AUTO_PROVISION` tax guia (ALC-1504):
  /// `Dr <paired debit slot> / Cr <tax liability slot>`, principal only, at the bill's competency
  /// date. Distinct from `statutory_payment_clearing`, which is the later payment leg against the
  /// bank, and from `ap_bill_provision`, which posts against AP payable from a human's
  /// classification — an auto-provisioned guia has no payable and no classification.
  'statutory_auto_provision',
  /// Approval-time Apurado → Em Guias reclassification for AUTO_PROVISION staged rails
  /// (Simples Nacional, ALC-1544). Distinct from the competency-date accrual journal.
  'statutory_auto_provision_reclassification',
  /// Approval-time staged-liability move for FGTS guides (ALC-1541):
  /// `Dr FGTS/Apurado / Cr FGTS/Em Guias a Pagar`, frozen per book and CoA version.
  'statutory_bypass_reclassification',
  'tax_liability_rail_migration',
  /// Capitalisation of a bill line into a `FixedAsset` when the review-queue candidate
  /// was decided AFTER the bill reached the GL (ALC-1673): `Dr <asset cost slot> /
  /// Cr <the expense account the bill already debited>`. Distinct from
  /// `ap_bill_provision`, which is where a candidate decided BEFORE approval lands —
  /// there the bill's own provisioning debit is redirected and no separate journal
  /// exists. Never touches the vendor payable: the payable is correct, only the side of
  /// the balance sheet the cost sits on is wrong.
  'fixed_asset_capitalization',
  /// One month's depreciation quota for one asset in one book (ALC-1674):
  /// `Dr <depreciation expense, COGS or EXPENSE per the asset class> /
  /// Cr <accumulated depreciation contra-asset>`. One journal per (asset, book,
  /// competência), posted from a schedule frozen at capitalisation. Books whose
  /// `postingMode` is `MEMO` never reach here — they have no `EntityBook`, their
  /// schedules terminate at `COMPUTED`, and the societária-fiscal delta they carry is
  /// read by query (ADR 0003 Decision 1).
  'fixed_asset_depreciation',
  /// Derecognition of one asset in one book (ALC-1675):
  /// `Dr <accumulated depreciation> + Dr <caixa/banco/a receber, when sold> /
  /// Cr <gross cost> + Cr <a pagar, when there were selling costs>`, with the difference
  /// debited or credited to the class's disposal-result account. One journal per (asset,
  /// book), and the amounts differ between books because accumulated depreciation does —
  /// the same sale can be a gain societariamente and a loss fiscalmente. Distinct from
  /// `journal_reversal`: this is not an unwinding of `fixed_asset_capitalization`, which
  /// would credit back the vendor payable the bill legitimately incurred. `MEMO` books get
  /// a `FixedAssetDisposalBookResult` row and no journal, for the reason
  /// `fixed_asset_depreciation` gives.
  'fixed_asset_disposal',
  /// Reclassification of the cost of a bem out of imobilizado em andamento into its final
  /// class (RUL-C1, CPC 27 §55): `Dr <target class cost> / Cr <CIP cost>`, per ledger book.
  'fixed_asset_transfer',
  /// Initial measurement of one lease in one book (ALC-1676):
  /// `Dr <right-of-use asset> / Cr <lease liability, split circulante and não
  /// circulante> + Cr <the payable or bank for the instalment paid at commencement> +
  /// Cr <initial direct costs> + Cr <restoration provision>`. One journal per (contract,
  /// book), and the debit deliberately exceeds the liability on a payment-in-advance
  /// contract: CPC 06 R2 §26 keeps the day-one instalment out of the liability and §24(b)
  /// puts it into the asset. Books whose `LeaseContractBookTreatment.recognitionMode` is
  /// `EXPENSE_PAYMENTS` never reach here — under ADR 0003 Decision 7 the BR fiscal régua
  /// deducts the contraprestação (Lei 12.973/2014 arts. 47-49) and carries no right of use
  /// at all, which is the temporary difference Parte A do e-Lalur reports.
  'lease_recognition',
  /// Balance-sheet reclassification of a lease liability as the reporting date moves
  /// (ALC-1677): `Dr <lease liability, não circulante> / Cr <lease liability, circulante>`
  /// for the principal that has come inside the twelve-month window since the split was
  /// last computed. CPC 26 §66-70 measures the split at the balance-sheet date, so ticket
  /// G's answer at commencement expires; this is what stops circulante staying understated
  /// for the whole term. A pure presentation entry — it touches no result account and
  /// changes no total. There is deliberately **no** settlement posting source alongside it:
  /// ADR 0003 Decision 4 settles an instalment by redirecting the debit of the recurring
  /// rent bill, so the settlement journal is the bill's own `ap_bill_provision`, with the
  /// line kept gross so the IRRF rails still compute from the contractual rent.
  'lease_liability_reclassification',
  'lease_remeasurement',
  /// Reclassification of one person's development-project share after the payroll run
  /// that carried their cost has already posted (ALC-3488, ADR 0017 D4):
  /// `Dr <intangível em andamento> / Cr <the payroll expense account the share came out
  /// of>`, or the reverse when a share shrinks. Posted in the **current open period**,
  /// never in the competência it corrects — the run is closed, may sit in a closed
  /// period, and is never reopened. Distinct from `payroll_run`, which is where an
  /// allocation known *before* the run posts lands: there the run's own rubric debit is
  /// split and no separate journal exists.
  'intangible_allocation_correction',
  /// Go-live of a development project (ALC-3489, ADR 0017 D6): one reclass moving the
  /// whole capitalised balance out of `FA_INTANGIBLE_IN_PROGRESS` into the class's cost
  /// account — `Dr <class cost> / Cr <intangível em andamento>` — with the project on
  /// `segmentJson` of both lines, so its position on the slot goes to exactly zero. The
  /// asset is born in the same transaction. Distinct from `fixed_asset_capitalization`,
  /// which reclassifies an ordinary expense on a bill nobody had routed to a project.
  'intangible_project_go_live',
  /// Abandonment of a development project (ALC-3489, ADR 0017 D7):
  /// `Dr FA_INTANGIBLE_WRITE_OFF_LOSS / Cr <intangível em andamento>` for the live
  /// capitalised balance, in the current open period. Not a reversal — the cost lines
  /// stay, because the money was really spent; what this records is that it never became
  /// an asset (CPC 04 §112).
  'intangible_project_write_off',
  'gl_onboarding_import',
  'payroll_run',
  'manual_journal',
  'journal_reversal',
  /// Expense accrual (provisão de competência) — journal-only, ADR 0016 / ALC-3214.
  'expense_accrual',
  /// Revenue Tax provision (ADR 0024 / ALC-4409): the PIS/COFINS provision journal linked to
  /// ONE revenue journal (`documentId` = the revenue journal id), same book/period/date:
  /// `Dr DEDUCAO_<TAX> / Cr <TAX> Apurado` (or the cash-basis "a diferir" slot).
  'revenue_tax_provision',
  /// Mirror of a Revenue Tax provision when its revenue journal is reversed, in the caller's
  /// reversal date/period (`documentId` = the caller's reversal journal id).
  'revenue_tax_provision_reversal',
  /// Cash basis (ALC-4414): release at receipt, `Dr <TAX> a realizar / Cr <TAX> Apurado`
  /// (`documentId` = the receipt journal id), and its mirror when the receipt is reversed.
  'revenue_tax_cash_release',
  'revenue_tax_cash_release_reversal',
  /// Cost Services (ALC-4475): the cost provision posted with ONE revenue journal, same
  /// book/period/date (`Dr cost / Cr cost-service liability`, vendor as line counterparty),
  /// and its mirror when that revenue journal is reversed (`documentId` = reversal journal).
  'cost_provision',
  'cost_provision_reversal',
  /// Cost Services (ALC-4476): the later match of a bill already approved as expense (story 48,
  /// `Dr provision / Cr the expense it replaces`, today) and the undo of a line's relief
  /// (`Dr the replaced expense / Cr provision`, today). `documentId` = the bill.
  'cost_match_reclass',
  'cost_match_undo',
  'cost_provision_close',
  /// Recognition and reversal of the recoverability allowance attached to deferred PIS/COFINS
  /// fixed-asset credit lots. The posting preserves the historical recoverable line dimensions and
  /// uses the mapped loss account; legal-tail application itself posts in `fixed_asset_disposal`
  /// because it is one compound derecognition journal.
  'fixed_asset_pis_cofins_recoverability_allowance',
  /// Approval of a taxpayer PIS/COFINS assessment: expense true-up, balance-sheet offsets, and
  /// branch/head-office transfers posted atomically by TaxAssessmentJournalPoster.
  'tax_assessment',
] as const;

/**
 * Writers that bypass the posting door and insert journal headers directly (GL-1 table, "Through
 * the door? no"). Not accepted by `JournalService`, but their journals reach the ledger.
 */
export const DIRECT_WRITE_POSTING_SOURCES = [
  'historical_razao_import',
  'offset_root_remediation',
  'credit_card_provider_gl_reclassification',
  /// Demo-only opening balances of the LARGE fixture's assets and lease (ALC-4616, epic ALC-4607):
  /// `Dr <asset cost or right-of-use asset> / Cr <saldos de implantação, a transitory liability>`,
  /// plus the lease's current and non-current liability legs. Written by
  /// `seed-large-asset-opening-balances` straight into `journal_header`, never by a product flow
  /// and never on a customer tenant.
  'asset_opening_balance',
] as const;

/**
 * Sources no producer in this tree writes any more, or that only seeders and one-off tenant scripts
 * wrote, but that posted journals still carry. Kept so the ledger never shows "Origem não
 * informada" for history.
 */
export const LEGACY_POSTING_SOURCES = [
  'ar_invoice',
  'manual',
  'manual_entry',
  'ap_payment_settlement',
  'credit_card_settlement',
  'revenue_classification_backfill',
  'managerial_chart_swap',
] as const;

export type JournalPostingSource = (typeof JOURNAL_POSTING_SOURCES)[number];

/** Every posting source a journal in the ledger can carry — the set the web must label. */
export const ALL_POSTING_SOURCES: readonly string[] = [
  ...JOURNAL_POSTING_SOURCES,
  ...DIRECT_WRITE_POSTING_SOURCES,
  ...LEGACY_POSTING_SOURCES,
];

const JOURNAL_POSTING_SOURCE_SET: ReadonlySet<string> = new Set(JOURNAL_POSTING_SOURCES);

/** True when `postingSource` is registered for the posting door. */
export function isRegisteredJournalPostingSource(postingSource: string): boolean {
  return JOURNAL_POSTING_SOURCE_SET.has(postingSource);
}
